"use client";

import { c as _c } from "react/compiler-runtime";
import { MapPin, Phone } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import type { MouseEvent } from "react";
import { memo, useCallback, useMemo } from "react";
import { TableSkeleton } from "@/components/skeleton/table-skeleton";
import { TableBodyRowEmpty } from "@/components/table-body-row-empty";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow, tableRowActionClassName } from "@/components/ui/table";
import { EMPTY_VALUE } from "@/lib/format";
import { maskHandler } from "@/lib/mask-handler";
import type { ProducerView } from "@/lib/producers";
import { cn } from "@/lib/utils";
import { ProducersTablePagination } from "./producers-table-pagination";
const TableHeaderComponent = memo(function TableHeaderComponentImpl() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <TableHeader className="sticky top-0 z-10"><TableRow><TableHead>Nome / Razão social</TableHead><TableHead>CPF / CNPJ</TableHead><TableHead className="min-w-64">Localização</TableHead><TableHead>Telefone</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
});
interface ProducerTableRowProps {
  producer: ProducerView;
  producerHref: Route;
  onNavigate: (href: Route) => void;
}
const ProducerTableRow = memo(function ProducerTableRowImpl(t0) {
  const $ = _c(59);
  const {
    producer,
    producerHref,
    onNavigate
  } = t0;
  const displayName = (producer.documentType === "cpf" ? producer.name : producer.companyName) ?? producer.displayName;
  let t1;
  if ($[0] !== producer.documentType || $[1] !== producer.documentValue) {
    t1 = maskHandler(producer.documentType.toUpperCase() as "CPF" | "CNPJ", producer.documentValue);
    $[0] = producer.documentType;
    $[1] = producer.documentValue;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const maskedDocument = t1;
  let t2;
  bb0: {
    if (!producer.address) {
      t2 = EMPTY_VALUE;
      break bb0;
    }
    let t3;
    if ($[3] !== producer.address.number || $[4] !== producer.address.street) {
      t3 = [producer.address.street, producer.address.number].filter(Boolean);
      $[3] = producer.address.number;
      $[4] = producer.address.street;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    const streetLine = t3.join(", ");
    const cityState = producer.address.city && producer.address.state ? `${producer.address.city} / ${producer.address.state}` : undefined;
    let t4;
    if ($[6] !== cityState || $[7] !== producer.address.neighborhood || $[8] !== streetLine) {
      t4 = [streetLine, producer.address.neighborhood, cityState].filter(_temp).join(" \u2022 ").trim() || EMPTY_VALUE;
      $[6] = cityState;
      $[7] = producer.address.neighborhood;
      $[8] = streetLine;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t2 = t4;
  }
  const addressDisplay = t2;
  let t3;
  if ($[10] !== producer.phone1 || $[11] !== producer.phone2) {
    bb1: {
      const phone = producer.phone1?.trim() || producer.phone2?.trim();
      if (!phone) {
        t3 = EMPTY_VALUE;
        break bb1;
      }
      t3 = maskHandler(phone.replace(/\D/g, "").length === 11 ? "celular" : "telefone", phone);
    }
    $[10] = producer.phone1;
    $[11] = producer.phone2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const phoneDisplay = t3;
  const isActive = producer.status === "active";
  let t4;
  if ($[13] !== onNavigate || $[14] !== producerHref) {
    t4 = event => {
      if ((event.target as HTMLElement).closest("a, button")) {
        return;
      }
      onNavigate(producerHref);
    };
    $[13] = onNavigate;
    $[14] = producerHref;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleClick = t4;
  const t5 = `producer-row-${producer.id}`;
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn(tableRowActionClassName, "block font-semibold");
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== displayName || $[18] !== producerHref) {
    t7 = <Link href={producerHref} className={t6}>{displayName}</Link>;
    $[17] = displayName;
    $[18] = producerHref;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== producer.email) {
    t8 = producer.email ? <p className="text-muted-foreground">{producer.email}</p> : null;
    $[20] = producer.email;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <TableCell>{t7}{t8}</TableCell>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== producer.documentType) {
    t10 = producer.documentType.toUpperCase();
    $[25] = producer.documentType;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10) {
    t11 = <Badge variant="outline">{t10}</Badge>;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== maskedDocument) {
    t12 = <span className="text-foreground font-medium tabular-nums">{maskedDocument}</span>;
    $[29] = maskedDocument;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12) {
    t13 = <TableCell><div className="flex items-center gap-2">{t11}{t12}</div></TableCell>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== addressDisplay) {
    t14 = addressDisplay === EMPTY_VALUE ? null : <MapPin aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />;
    $[34] = addressDisplay;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== addressDisplay) {
    t15 = <span className="text-foreground">{addressDisplay}</span>;
    $[36] = addressDisplay;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <TableCell><div className="flex items-center gap-2">{t14}{t15}</div></TableCell>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== phoneDisplay) {
    t17 = phoneDisplay === EMPTY_VALUE ? null : <Phone aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />;
    $[41] = phoneDisplay;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== phoneDisplay) {
    t18 = <span className="text-foreground tabular-nums">{phoneDisplay}</span>;
    $[43] = phoneDisplay;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t17 || $[46] !== t18) {
    t19 = <TableCell><div className="flex items-center gap-2">{t17}{t18}</div></TableCell>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  const t20 = isActive ? "status-active" : "status-inactive";
  const t21 = isActive ? "Ativo" : "Inativo";
  let t22;
  if ($[48] !== t20 || $[49] !== t21) {
    t22 = <TableCell><Badge variant={t20}>{t21}</Badge></TableCell>;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== handleClick || $[52] !== t13 || $[53] !== t16 || $[54] !== t19 || $[55] !== t22 || $[56] !== t5 || $[57] !== t9) {
    t23 = <TableRow className="cursor-pointer" data-testid={t5} onClick={handleClick}>{t9}{t13}{t16}{t19}{t22}</TableRow>;
    $[51] = handleClick;
    $[52] = t13;
    $[53] = t16;
    $[54] = t19;
    $[55] = t22;
    $[56] = t5;
    $[57] = t9;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  return t23;
});
export const ProducersTable = memo(function ProducersTableImpl(t0) {
  const $ = _c(31);
  const {
    data,
    hasActiveFilters,
    paginationInfo,
    isLoading,
    isStale: t1,
    prefetchNextPage,
    updateSearchParams
  } = t0;
  const isStale = t1 === undefined ? false : t1;
  const router = useRouter();
  const {
    organizationSlug
  } = useParams();
  let t2;
  if ($[0] !== router) {
    t2 = href => {
      router.push(href);
    };
    $[0] = router;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleNavigate = t2;
  let t3;
  if ($[2] !== data || $[3] !== handleNavigate || $[4] !== organizationSlug) {
    let t4;
    if ($[6] !== handleNavigate || $[7] !== organizationSlug) {
      t4 = producer => {
        const producerHref = `/${organizationSlug}/producers/${producer.id}` as Route;
        return <ProducerTableRow key={producer.id} onNavigate={handleNavigate} producer={producer} producerHref={producerHref} />;
      };
      $[6] = handleNavigate;
      $[7] = organizationSlug;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = data.map(t4);
    $[2] = data;
    $[3] = handleNavigate;
    $[4] = organizationSlug;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const rows = t3;
  if (isLoading) {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <TableHeaderComponent />;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Table>{t4}<TableBody><TableSkeleton columns={5} rows={8} /></TableBody></Table>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== isStale) {
      t6 = <TableContainer isStale={isStale}>{t5}</TableContainer>;
      $[11] = isStale;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TableHeaderComponent />;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== data.length || $[15] !== hasActiveFilters || $[16] !== rows) {
    t5 = data.length ? rows : <TableBodyRowEmpty columns={5} message={hasActiveFilters ? "Nenhum produtor corresponde aos filtros." : "Nenhum produtor cadastrado."} />;
    $[14] = data.length;
    $[15] = hasActiveFilters;
    $[16] = rows;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t5) {
    t6 = <Table>{t4}<TableBody>{t5}</TableBody></Table>;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== isStale || $[21] !== t6) {
    t7 = <TableContainer isStale={isStale}>{t6}</TableContainer>;
    $[20] = isStale;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== data.length || $[24] !== paginationInfo || $[25] !== prefetchNextPage || $[26] !== updateSearchParams) {
    t8 = paginationInfo && data.length ? <ProducersTablePagination hasMore={paginationInfo.hasMore} limit={paginationInfo.limit} offset={paginationInfo.offset} prefetchNextPage={prefetchNextPage} total={paginationInfo.total} updateSearchParams={updateSearchParams} /> : null;
    $[23] = data.length;
    $[24] = paginationInfo;
    $[25] = prefetchNextPage;
    $[26] = updateSearchParams;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t7 || $[29] !== t8) {
    t9 = <div className="flex flex-col gap-6">{t7}{t8}</div>;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
});
function _temp(value) {
  return value && value.trim().length > 0;
}